/**
 * composition/compile.ts
 *
 * Implements `skaile rebuild <agent>` — re-snapshots all inline-snapshot items,
 * bumps the prompt version, and writes `<agent>/compile_manifest.json`.
 *
 * The compile manifest records what was resolved and when, so the runner can
 * verify content hasn't drifted without a rebuild.
 */

import { createHash } from "node:crypto";
import fs from "node:fs/promises";
import path from "node:path";
import type { AgentComposeItem } from "@skaile/workspaces/types/manifests";
import { type CompileManifest, type CompileManifestEntry, resolveBinding } from "./types.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Filename for the agent compile manifest written by `skaile rebuild`.
 *
 * @docLink packages/runner/capabilities#compile-composition
 */
export const COMPILE_MANIFEST_FILENAME = "compile_manifest.json";

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Options for {@link compileComposition}.
 *
 * @docLink packages/runner/capabilities#compile-composition
 */
export interface CompileOpts {
  /** Path to the agent directory (where agent.yaml and compile_manifest.json live) */
  agentDir: string;
  /** The agent's composes: items */
  items: AgentComposeItem[];
  /**
   * Content loader — given an asset ref + kind, returns the content text.
   * Returns null if the content cannot be loaded.
   */
  loadContent: (ref: string, kind: string) => Promise<string | null>;
  /**
   * Version extractor — given an asset ref, returns the resolved version string.
   * Returns null if unknown.
   */
  resolveVersion?: (ref: string) => Promise<string | null>;
}

/**
 * Result returned by {@link compileComposition}.
 *
 * @docLink packages/runner/capabilities#compile-composition
 */
export interface CompileResult {
  /** The written compile manifest */
  manifest: CompileManifest;
  /** Path to the written file */
  manifestPath: string;
  /** Number of entries that were successfully resolved */
  resolvedCount: number;
  /** Warnings for unresolvable items */
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compile (rebuild) the agent's composition.
 *
 * 1. Reads existing compile_manifest.json (if present) for version continuity
 * 2. Re-resolves ALL items (both snapshot and non-snapshot get recorded)
 * 3. Bumps prompt_version
 * 4. Writes compile_manifest.json
 *
 * @param opts - Compile options including agentDir, items, and content loader.
 * @returns A {@link CompileResult} with the written manifest and any warnings.
 * @docLink packages/runner/capabilities#compile-composition
 */
export async function compileComposition(opts: CompileOpts): Promise<CompileResult> {
  const { agentDir, items, loadContent, resolveVersion } = opts;
  const manifestPath = path.join(agentDir, COMPILE_MANIFEST_FILENAME);
  const warnings: string[] = [];

  // Read existing manifest for version continuity
  const existing = await loadCompileManifest(manifestPath);
  const nextVersion = (existing?.prompt_version ?? 0) + 1;

  // Resolve all items
  const entries: CompileManifestEntry[] = [];
  for (const item of items) {
    const binding = resolveBinding(item);
    const content = await loadContent(item.ref, item.kind);

    if (!content) {
      warnings.push(`"${item.ref}" (${item.kind}): content not found — skipped`);
      continue;
    }

    const hash = createHash("sha256").update(content).digest("hex");
    const version = resolveVersion ? await resolveVersion(item.ref) : null;

    entries.push({
      asset_ref: item.ref,
      version,
      hash,
      binding,
      resolved_at: new Date().toISOString(),
    });
  }

  const manifest: CompileManifest = {
    schema_version: 1,
    prompt_version: nextVersion,
    entries,
    compiled_at: new Date().toISOString(),
  };

  // Write atomically: temp file + rename to avoid corruption on interrupt
  const tmpPath = `${manifestPath}.tmp`;
  await fs.writeFile(tmpPath, `${JSON.stringify(manifest, null, 2)}\n`, "utf-8");
  await fs.rename(tmpPath, manifestPath);

  return {
    manifest,
    manifestPath,
    resolvedCount: entries.length,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Load an existing compile_manifest.json, or return undefined if not found.
 *
 * @param manifestPath - Absolute path to the manifest file.
 * @returns The parsed {@link CompileManifest} or `undefined` when missing or malformed.
 * @docLink packages/runner/capabilities#load-compile-manifest
 */
export async function loadCompileManifest(
  manifestPath: string,
): Promise<CompileManifest | undefined> {
  try {
    const raw = await fs.readFile(manifestPath, "utf-8");
    const parsed = JSON.parse(raw);
    if (parsed?.schema_version === 1 && typeof parsed.prompt_version === "number") {
      return parsed as CompileManifest;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Load compile manifest from an agent directory, reading the file from
 * `<agentDir>/compile_manifest.json`.
 *
 * @param agentDir - Absolute path to the agent definition directory.
 * @returns The parsed {@link CompileManifest} or `undefined` when missing or malformed.
 * @docLink packages/runner/capabilities#load-compile-manifest-from-dir
 */
export async function loadCompileManifestFromDir(
  agentDir: string,
): Promise<CompileManifest | undefined> {
  return loadCompileManifest(path.join(agentDir, COMPILE_MANIFEST_FILENAME));
}
